/* =============================================================
   Sweet Smiling Soul — Cart Upsell  ·  "Add a Pattern, 50% off"
   -------------------------------------------------------------
   Two placements, one script:
     1) Cart notification popup  ->  #cart-upsell
     2) Cart page (/cart)        ->  injected block

   UX model (Jul 2026):
   - POPUP: ticking is instant and local-only (light purple row, stored
     in sessionStorage). The ticked patterns are added and the 50% codes
     applied in one go when the customer clicks "Checkout" or
     "View cart". Fail OPEN: checkout is never blocked.
   - CART PAGE: ticking updates the cart IMMEDIATELY. The row turns
     light purple instantly, then the item is added/removed, the code
     applied, and the cart items + totals are refreshed in place via
     the Section Rendering API — no page reload. Any popup selections
     that weren't committed yet are committed on arrival.

   Discount: two generic Buy X Get Y CODES, applied ONLY for ticked
   add-ons (never store-wide). All patterns share the same price, so:
       1 add-on ticked  -> ADDPATTERN50OFF   (buy 1 pattern/bundle,
                                              get 1 pattern 50% off)
       2 add-ons ticked -> ADDPATTERN50OFFX2 (buy 1 pattern/bundle,
                                              get 2 patterns 50% off)
   BXGY needs a qualifying "buy" item, so the anchor can't be the
   discounted unit and a lone pattern can't be half-priced. Shopify
   picks which eligible line the 50% lands on — since all patterns cost
   the same, the total is identical either way.

   Codes are applied/removed with the Cart AJAX API:
       POST /cart/update.js  { discount: 'CODE1,CODE2' }   (Shopify, May 2025)
   NOTE: the discount param ADDS codes — syncDiscountCodes clears the
   set first, then applies exactly the desired codes.

   Add a 4th pattern later: add a row to PATTERNS below (with a short
   name + blurb), add the new product to BOTH discount codes' buy/get
   product lists in Shopify admin, and add it to the "Doll & Book
   Sewing Patterns" collection.
   ============================================================= */
class CartUpsell {
  constructor() {
    this.DISCOUNT_PCT = 0.5;

    /* inBundle: true  -> the pattern is included in the bundle, so it is
       never offered as an add-on when the bundle is in the cart.
       Playtime is NOT in the bundle, so it stays on offer for bundle
       buyers (its BXGY code accepts the bundle as the anchor). */
    this.PATTERNS = [
      { key: 'dollhouse', inBundle: true, name: '🏠 Dollhouse Storybook', handle: 'doll-fabric-dollhouse-sewing-pattern-beginner-pdf-pattern-diy-dollhouse-storybook-scrap-fabric-project-handmade-doll-pattern', blurb: 'Sewing pattern for a 14cm doll (girl and boy versions included) + a foldable fabric dollhouse book with 6 rooms to decorate: bedroom, bathroom, living room, dining room, hobby room, and wardrobe.' },
      { key: 'adventure', inBundle: true, name: '⛺️ Adventure Book', handle: 'diy-fabric-doll-and-adventure-book-sewing-pattern-beginner-friendly-pdf-pattern', blurb: 'Sewing pattern for a 14cm doll (girl and boy versions included) + a foldable fabric book with 7 outdoor adventure scenes: campsite, hammock, beach, treehouse and more.' },
      { key: 'playtime',  inBundle: false, name: '🧸 Playtime Storybook', handle: 'diy-fabric-doll-and-playtime-storybook-sewing-pattern-beginner-friendly-digital', blurb: 'Sewing pattern for a 14cm doll (girl and boy versions included) + a foldable fabric book with 8 playful scenes and fun tiny accessories: costume box, tea party, painting station, music room and more.' }
    ];
    this.BUNDLE_HANDLE = 'bundle-diy-fabric-doll-and-adventure-book-sewing-pattern-beginner-friendly-pdf-pattern';

    // one generic customer-facing code per case (see header comment)
    this.SINGLE_CODE = 'ADDPATTERN50OFF';
    this.DOUBLE_CODE = 'ADDPATTERN50OFFX2';

    this.productCache = {};
    this.busy = false;
    this.debounceTimer = null;
    this._queue = Promise.resolve();
    this._cartCount = 0; // last known real cart item count (popup context)
    this._cartPageTimer = null;
    this._inflight = 0; // cart-page toggles currently being processed

    this.injectStyles();
    this.observeNotification();
    if (this.isCartPage()) this.initCartPage();
  }

  /* ---------------- helpers ---------------- */
  isCartPage() {
    var p = window.location.pathname.replace(/\/+$/, '');
    return p === '/cart' || p.slice(-5) === '/cart' || !!document.querySelector('#main-cart-footer');
  }

  formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
  }

  cleanTitle(t) {
    return (t || '').replace(/^[^A-Za-z0-9]+/, '').replace(/^(NEW)\s*/i, '').trim();
  }

  getCart() { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }); }

  getProduct(handle) {
    var self = this;
    if (this.productCache[handle]) return Promise.resolve(this.productCache[handle]);
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var data = {
          handle: handle,
          title: p.title,
          variantId: p.variants[0].id,
          price: p.variants[0].price,
          image: p.featured_image || (p.images && p.images[0]) || ''
        };
        self.productCache[handle] = data;
        return data;
      })
      .catch(function () { return null; });
  }

  patternByKey(k) { for (var i = 0; i < this.PATTERNS.length; i++) if (this.PATTERNS[i].key === k) return this.PATTERNS[i]; return null; }

  patternByHandle(handle) { for (var i = 0; i < this.PATTERNS.length; i++) if (this.PATTERNS[i].handle === handle) return this.PATTERNS[i]; return null; }

  patternByHandleKey(handle) {
    for (var i = 0; i < this.PATTERNS.length; i++) if (this.PATTERNS[i].handle === handle) return this.PATTERNS[i].key;
    return '';
  }

  patternsState(cart) {
    var handlesInCart = (cart.items || []).map(function (i) { return i.handle; });
    var inCart = [], missing = [];
    this.PATTERNS.forEach(function (pat) {
      if (handlesInCart.indexOf(pat.handle) !== -1) inCart.push(pat); else missing.push(pat);
    });
    return { inCart: inCart, missing: missing, bundleInCart: handlesInCart.indexOf(this.BUNDLE_HANDLE) !== -1 };
  }

  // which patterns may be offered right now: all missing ones normally;
  // with the bundle in the cart, only patterns the bundle doesn't include
  offersFor(state) {
    if (state.bundleInCart) {
      return state.missing.filter(function (p) { return !p.inBundle; });
    }
    return state.missing;
  }

  // serialize cart mutations so rapid ticks can't race each other
  enqueue(fn) {
    this._queue = this._queue.then(fn).catch(function () {});
    return this._queue;
  }

  /* ---- pending selections (popup only: ticked, not yet in the cart) ---- */
  getPending() { try { return JSON.parse(sessionStorage.getItem('sss_pending') || '[]'); } catch (e) { return []; } }
  setPending(a) { try { sessionStorage.setItem('sss_pending', JSON.stringify(a)); } catch (e) {} }
  addPending(key) { var a = this.getPending(); if (a.indexOf(key) === -1) { a.push(key); this.setPending(a); } }
  removePending(key) { this.setPending(this.getPending().filter(function (k) { return k !== key; })); }

  // keep pending in sync with reality: drop keys for patterns that are no
  // longer offered (already in cart, or the bundle covers them)
  prunePending(state) {
    var offerKeys = this.offersFor(state).map(function (p) { return p.key; });
    this.setPending(this.getPending().filter(function (k) { return offerKeys.indexOf(k) !== -1; }));
  }

  /* ---- committed add-ons (ticked AND added to the cart) ---- */
  getAddons() { try { return JSON.parse(sessionStorage.getItem('sss_addons') || '[]'); } catch (e) { return []; } }
  setAddons(a) { try { sessionStorage.setItem('sss_addons', JSON.stringify(a)); } catch (e) {} }
  getChannels() { try { return JSON.parse(sessionStorage.getItem('sss_addon_channels') || '{}'); } catch (e) { return {}; } }
  setChannels(m) { try { sessionStorage.setItem('sss_addon_channels', JSON.stringify(m)); } catch (e) {} }
  addAddon(key, channel) {
    var a = this.getAddons();
    if (a.indexOf(key) === -1) { a.push(key); this.setAddons(a); }
    var m = this.getChannels();
    m[key] = channel || m[key] || 'notification';
    this.setChannels(m);
  }
  removeAddon(key) {
    this.setAddons(this.getAddons().filter(function (k) { return k !== key; }));
    var m = this.getChannels();
    delete m[key];
    this.setChannels(m);
  }

  // one add-on -> the single code; two add-ons -> the x2 code
  codesForCommitted(committed) {
    if (committed.length >= 2) return [this.DOUBLE_CODE];
    if (committed.length === 1) return [this.SINGLE_CODE];
    return [];
  }

  // codes for committed add-ons that are STILL in the cart
  addonCodes(cart) {
    var handles = (cart.items || []).map(function (i) { return i.handle; });
    var self = this;
    var committed = this.getAddons()
      .map(function (k) { return self.patternByKey(k); })
      .filter(function (p) { return p && handles.indexOf(p.handle) !== -1; });
    return this.codesForCommitted(committed);
  }

  appliedSig() { try { return sessionStorage.getItem('sss_applied_sig') || ''; } catch (e) { return ''; } }
  setAppliedSig(s) { try { sessionStorage.setItem('sss_applied_sig', s); } catch (e) {} }

  /* ---- apply/remove discount codes + tracking attributes (one call) ----
     Cart attributes land on the order as note attributes ("Additional
     details" in admin) and are readable by Shopify Flow for tagging:
       pattern_upsell        -> "dollhouse:notification,playtime:cart_page"
       pattern_upsell_count  -> "1" / "2"
       pattern_upsell_anchor -> handles of the non-add-on items in the cart
  */
  syncDiscountCodes() {
    var self = this;
    return this.getCart().then(function (cart) {
      var handles = (cart.items || []).map(function (i) { return i.handle; });
      var committed = self.getAddons()
        .map(function (k) { return self.patternByKey(k); })
        .filter(function (p) { return p && handles.indexOf(p.handle) !== -1; });

      // prune addon keys whose line item is gone, so stale codes never return
      self.setAddons(committed.map(function (p) { return p.key; }));

      var codes = self.codesForCommitted(committed);
      var channels = self.getChannels();
      var summary = committed.map(function (p) {
        return p.key + ':' + (channels[p.key] || 'notification');
      }).join(',');
      var anchors = (cart.items || [])
        .filter(function (i) { return !(i.properties && i.properties._addon); })
        .map(function (i) { return i.handle; })
        .join(',');

      self.setAppliedSig(codes.slice().sort().join(','));

      var post = function (payload) {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (r) { return r.json(); }).catch(function () { return null; });
      };

      var attributes = {
        pattern_upsell: summary,
        pattern_upsell_count: committed.length ? String(committed.length) : '',
        pattern_upsell_anchor: committed.length ? anchors : ''
      };

      /* IMPORTANT: the `discount` param ADDS codes to the cart's existing
         set — it does not replace it. Stale codes (e.g. a single code left
         behind after switching to a pair code, or after removing a line)
         conflict with the new one and can block the 50% from applying.
         So: clear ALL codes first, then apply exactly the desired set. */
      var clearStep = post({ discount: '' });
      return clearStep.then(function () {
        return post({
          discount: codes.length ? codes.join(',') : '',
          attributes: attributes
        });
      }).then(function () { return cart; });
    });
  }

  refreshCartCount() {
    this.getCart().then(function (cart) {
      document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
        b.querySelectorAll('span').forEach(function (s) {
          if (!s.classList.contains('visually-hidden')) s.textContent = cart.item_count;
        });
      });
    });
  }

  /* ---- refresh cart page items + totals in place (no reload) ---- */
  refreshCartSections() {
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    var itemsId = itemsEl ? itemsEl.dataset.id : null;
    var footerId = footerEl ? footerEl.dataset.id : null;
    var ids = [];
    if (itemsId) ids.push(itemsId);
    if (footerId && footerId !== itemsId) ids.push(footerId);
    if (!ids.length) { window.location.reload(); return Promise.resolve(); }

    return fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var parser = new DOMParser();
        [[itemsId, '#main-cart-items'], [footerId, '#main-cart-footer']].forEach(function (pair) {
          var id = pair[0];
          if (!id || !sections[id]) return;
          var doc = parser.parseFromString(sections[id], 'text/html');
          var source = doc.querySelector(pair[1] + ' .js-contents') || doc.querySelector('.js-contents');
          var target = document.querySelector(pair[1] + ' .js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
        });
      })
      .catch(function () { window.location.reload(); });
  }

  /* ---------------- commit pending (popup CTAs) ---------------- */
  lockCta(el, label) {
    if (!el) return;
    el.setAttribute('aria-busy', 'true');
    el.style.pointerEvents = 'none';
    el.style.opacity = '.7';
    if (el.tagName === 'BUTTON') el.disabled = true;
    if (label) el.textContent = label;
  }

  // add the given patterns (that aren't in the cart yet) in one call
  addPatterns(patterns, cart) {
    var self = this;
    var handles = (cart.items || []).map(function (i) { return i.handle; });
    var toAdd = patterns.filter(function (p) { return p && handles.indexOf(p.handle) === -1; });
    if (!toAdd.length) return Promise.resolve(null);
    return Promise.all(toAdd.map(function (p) { return self.getProduct(p.handle); })).then(function (prods) {
      var items = prods.filter(Boolean).map(function (p) {
        return { id: p.variantId, quantity: 1, properties: { _addon: 'Add-a-pattern 50% off' } };
      });
      if (!items.length) return null;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) { return r.json(); });
    });
  }

  commitPending(nextUrl, ctaEl) {
    var self = this;
    if (this.busy) return;
    this.busy = true;
    this.lockCta(ctaEl, 'Adding your pattern…');

    var go = function () { window.location.href = nextUrl; };

    this.getCart().then(function (cart) {
      var pend = self.getPending().map(function (k) { return self.patternByKey(k); }).filter(Boolean);
      return self.addPatterns(pend, cart).then(function () {
        pend.forEach(function (p) { self.addAddon(p.key, 'notification'); });
        self.setPending([]);
        return self.syncDiscountCodes();
      });
    }).then(go).catch(go); // fail open — never block checkout
  }

  /* ---------------- shared markup ---------------- */
  upsellMarkup(products, context, bundleInCart) {
    var self = this;
    var pending = this.getPending();
    var rows = products.map(function (p) {
      var full = self.formatMoney(p.price);
      var half = self.formatMoney(Math.round(p.price * self.DISCOUNT_PCT));
      var img  = p.image ? (p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=160') : '';
      var pat  = self.patternByHandle(p.handle);
      var blurb = pat && pat.blurb ? pat.blurb : '';
      var title = pat && pat.name ? pat.name : self.cleanTitle(p.title);
      var key = self.patternByHandleKey(p.handle);
      var selected = context === 'notification' && pending.indexOf(key) !== -1;
      return (
        '<label class="sss-bump' + (selected ? ' is-selected' : '') + '" data-key="' + key + '">' +
          '<input type="checkbox" class="sss-bump__check" data-key="' + key + '"' + (selected ? ' checked' : '') + '>' +
          (img ? '<img class="sss-bump__img" src="' + img + '" alt="" width="64" height="64" loading="lazy">' : '') +
          '<span class="sss-bump__text">' +
            '<span class="sss-bump__title">Yes! Add the ' + title + '</span>' +
            (blurb ? '<span class="sss-bump__blurb">' + blurb + '</span>' : '') +
            '<span class="sss-bump__price"><s>' + full + '</s> <strong>' + half + '</strong> <em>50% off</em></span>' +
          '</span>' +
        '</label>'
      );
    }).join('');

    var heading;
    if (bundleInCart) {
      heading = 'Complete the collection — add the Playtime Storybook for 50% off';
    } else if (products.length > 1) {
      heading = 'Complete your set &amp; save 50%';
    } else {
      heading = 'Add the matching pattern for 50% off';
    }

    var note = context === 'notification'
      ? 'Ticked patterns are added when you continue to checkout. The 50% discount applies automatically, no code needed. Offer is not available anywhere else.'
      : 'Ticked patterns are added to your cart instantly at 50% off — no code needed. Offer is not available anywhere else.';

    return (
      '<div class="sss-upsell">' +
        '<p class="sss-upsell__head">★ ' + heading + '</p>' +
        '<p class="sss-upsell__sub">These patterns are made to go together. The same little 14cm doll plays across every book, so you can build a whole world for them to explore. Tick to add another beginner-friendly pattern at <strong>half price</strong>. Every pattern is an instant PDF download with free step-by-step written, photo, and video tutorials, made to sew from felt and fabric scraps you already have.</p>' +
        rows +
        '<p class="sss-bump__note">' + note + '</p>' +
      '</div>'
    );
  }

  injectStyles() {
    if (document.getElementById('sss-upsell-styles')) return;
    var css =
      /* --- popup: wider on tablet/desktop, one scroll column, CTA always visible --- */
      '@media screen and (min-width:750px){.cart-notification{max-width:46rem !important;}}' +
      '#cart-notification{max-height:calc(100vh - 70px);max-height:calc(100dvh - 70px);overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;}' +
      '.cart-notification-product{padding-top:1.2rem !important;padding-bottom:1.2rem !important;}' +
      /* sticky primary CTA inside the scrolling popup */
      '.sss-cta-sticky{position:sticky;bottom:0;z-index:2;background:rgb(var(--color-background));margin:0 -3.5rem;padding:.8rem 3.5rem calc(.8rem + env(safe-area-inset-bottom,0px));box-shadow:0 -6px 12px -8px rgba(0,0,0,.18);}' +
      '.sss-trust{display:block;margin:.7rem 0 0;font-size:1.05rem;line-height:1.4;color:rgba(var(--color-foreground),.7);text-align:center;}' +
      /* upsell block: no nested scrolling — the popup itself scrolls */
      '#cart-upsell{display:block;margin:0 0 1rem;}' +
      '#main-cart-items .cart-upsell{display:none !important;}' +
      '.sss-upsell-wrap{margin:2rem 0 .5rem;}' +
      '.sss-upsell-wrap .sss-upsell{max-width:62rem;margin-left:auto;margin-right:auto;}' +
      '.sss-upsell{border:1px solid #f0d9e2;background:#fff7fb;border-radius:12px;padding:14px 16px;margin:0 0 16px;text-align:left;}' +
      '.sss-upsell__head{margin:0 0 4px;font-weight:700;color:#46523e;font-size:1.45rem;}' +
      '.sss-upsell__sub{margin:0 0 12px;color:#6b7363;font-size:1.15rem;line-height:1.45;}' +
      '@media screen and (max-width:479px){.sss-upsell{padding:12px;}.sss-upsell__sub{margin-bottom:10px;}}' +
      '.sss-bump{display:flex;align-items:flex-start;gap:12px;border:1px solid #e8dfe4;background:#fff;border-radius:10px;padding:12px;margin-bottom:10px;cursor:pointer;transition:border-color .15s,background .15s,box-shadow .15s;}' +
      '.sss-bump:last-of-type{margin-bottom:0;}' +
      '@media (hover:hover){.sss-bump:hover{border-color:#c9a3d8;box-shadow:0 1px 4px rgba(181,143,214,.25);}}' +
      /* selected = light purple, instantly, and it stays that way */
      '.sss-bump.is-selected{border-color:#b58fd6;background:#f6effb;}' +
      '.sss-bump input{width:22px;height:22px;accent-color:#a678c9;flex:none;cursor:pointer;margin:2px 0 0;}' +
      '.sss-bump__img{border-radius:8px;object-fit:cover;flex:none;width:64px;height:64px;}' +
      '@media screen and (max-width:479px){.sss-bump__img{width:56px;height:56px;}}' +
      '.sss-bump__text{display:flex;flex-direction:column;gap:3px;min-width:0;}' +
      '.sss-bump__title{font-weight:600;color:#46523e;font-size:1.25rem;line-height:1.3;}' +
      '.sss-bump__blurb{font-size:1.12rem;color:#7a7f72;line-height:1.4;}' +
      '.sss-bump__price{font-size:1.2rem;color:#6b7363;margin-top:1px;}' +
      '.sss-bump__price s{color:#b9a0a8;margin-right:5px;}' +
      '.sss-bump__price strong{color:#2d5a2d;font-size:1.35rem;}' +
      '.sss-bump__price em{display:inline-block;background:#2d5a2d;color:#fff;font-style:normal;font-size:1.0rem;font-weight:700;padding:1px 8px;border-radius:20px;margin-left:5px;vertical-align:middle;}' +
      '.sss-bump__note{margin:8px 0 0;font-size:1.05rem;color:#8a8f82;}';
    var style = document.createElement('style');
    style.id = 'sss-upsell-styles';
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  bindBumps(scope, context) {
    var self = this;
    scope.querySelectorAll('.sss-bump__check').forEach(function (input) {
      input.addEventListener('change', function () {
        var key = input.dataset.key;
        var row = input.closest('.sss-bump');
        var checked = input.checked;
        // instant visual state, both contexts
        if (row) row.classList.toggle('is-selected', checked);

        if (context === 'notification') {
          // popup: local-only; committed on Checkout / View cart
          if (checked) self.addPending(key); else self.removePending(key);
          self.updateViewCartCount();
          return;
        }

        // cart page: update the cart right away (queued to avoid races)
        self._inflight++;
        self.enqueue(function () {
          return self.applyCartPageToggle(key, checked, input, row)
            .then(function () { self._inflight--; }, function () { self._inflight--; });
        });
      });
    });
  }

  // cart page: add/remove the pattern, sync codes, refresh totals in place
  applyCartPageToggle(key, checked, input, row) {
    var self = this;
    var pat = this.patternByKey(key);
    if (!pat) return Promise.resolve();

    return this.getProduct(pat.handle).then(function (p) {
      if (!p) throw new Error('product unavailable');
      return checked
        ? fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1, properties: { _addon: 'Add-a-pattern 50% off' } }] }) })
        : fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: p.variantId, quantity: 0 }) });
    }).then(function (r) { return r.json(); }).then(function () {
      if (checked) self.addAddon(key, 'cart_page'); else self.removeAddon(key);
      return self.syncDiscountCodes();
    }).then(function () {
      return self.refreshCartSections();
    }).then(function () {
      self.refreshCartCount();
    }).catch(function () {
      // revert the checkbox if anything failed
      input.checked = !checked;
      if (row) row.classList.toggle('is-selected', !checked);
    });
  }

  /* ---------------- placement 1: notification popup ---------------- */
  observeNotification() {
    var target = document.querySelector('cart-notification') || document.getElementById('CartNotification');
    if (!target) return;
    var self = this;
    new MutationObserver(function () {
      clearTimeout(self.debounceTimer);
      self.debounceTimer = setTimeout(function () { self.renderNotification(); }, 120);
    }).observe(target, { childList: true, subtree: true });
  }

  renderNotification() {
    var container = document.getElementById('cart-upsell');
    if (!container) return;
    var self = this;
    this.getCart().then(function (cart) {
      if (!cart.items || !cart.items.length) { container.innerHTML = ''; container.dataset.sig = ''; return; }
      var state = self.patternsState(cart);
      self.prunePending(state);
      self._cartCount = cart.item_count || 0;
      self.updateViewCartCount();
      var offers = self.offersFor(state);
      var hasAnchor = state.bundleInCart || state.inCart.length >= 1;
      var show = hasAnchor && offers.length >= 1;
      if (!show) { container.innerHTML = ''; container.dataset.sig = ''; return; }
      var sig = 'n:' + offers.map(function (p) { return p.key; }).join(',');
      if (container.dataset.sig === sig) return;
      container.dataset.sig = sig;

      Promise.all(offers.map(function (p) { return self.getProduct(p.handle); })).then(function (prods) {
        prods = prods.filter(Boolean);
        if (!prods.length) { container.dataset.sig = ''; return; }
        container.innerHTML = self.upsellMarkup(prods, 'notification', state.bundleInCart);
        self.bindBumps(container, 'notification');
        self.bindNotificationCommit();
      });
    });
  }

  /* keep the "View cart (N)" label = real cart items + pending ticks.
     Only writes when the text actually changes, so the MutationObserver
     on the popup can't loop. */
  updateViewCartCount() {
    var btn = document.getElementById('cart-notification-button');
    if (!btn) return;
    var total = (this._cartCount || 0) + this.getPending().length;
    if (!total) return;
    var current = btn.textContent;
    var next = /\(\d+\)/.test(current)
      ? current.replace(/\(\d+\)/, '(' + total + ')')
      : current.trim() + ' (' + total + ')';
    if (current !== next) btn.textContent = next;
  }

  bindNotificationCommit() {
    var self = this;

    // Checkout: add pending patterns + codes first, then continue to checkout
    var form = document.getElementById('cart-notification-form');
    if (form && !form.dataset.sssBound) {
      form.dataset.sssBound = '1';
      form.addEventListener('submit', function (e) {
        if (!self.getPending().length) return; // nothing pending — normal checkout
        e.preventDefault();
        self.commitPending('/checkout', form.querySelector('button'));
      });
    }

    // View cart: same, then continue to the cart page
    var viewCart = document.getElementById('cart-notification-button');
    if (viewCart && !viewCart.dataset.sssBound) {
      viewCart.dataset.sssBound = '1';
      viewCart.addEventListener('click', function (e) {
        if (!self.getPending().length) return; // nothing pending — normal link
        e.preventDefault();
        self.commitPending('/cart', viewCart);
      });
    }
  }

  /* if the customer ticks a box and clicks Checkout before the add +
     discount calls finish, hold the navigation until they complete —
     otherwise the discount update is cancelled mid-flight */
  bindCartPageGuard() {
    var self = this;
    var btn = document.getElementById('checkout');
    if (!btn || btn.dataset.sssGuard) return;
    btn.dataset.sssGuard = '1';
    btn.addEventListener('click', function (e) {
      if (self._inflight === 0) return; // nothing in flight — normal checkout
      e.preventDefault();
      self.lockCta(btn, 'One moment…');
      self._queue.then(function () { window.location.href = '/checkout'; });
    });
  }

  /* ---------------- placement 2: cart page ---------------- */
  initCartPage() {
    var self = this;
    this.observeCartPage();
    this.bindCartPageGuard();
    this.getCart().then(function (cart) {
      if (!cart.items || !cart.items.length) return;
      var state = self.patternsState(cart);
      self.prunePending(state);

      // commit any selections ticked in the popup but not committed yet
      // (e.g. the customer used the header cart icon instead of "View cart")
      var pend = self.getPending().map(function (k) { return self.patternByKey(k); }).filter(Boolean);
      var commitStep = pend.length
        ? self.addPatterns(pend, cart).then(function () {
            pend.forEach(function (p) { self.addAddon(p.key, 'notification'); });
            self.setPending([]);
          })
        : Promise.resolve();

      commitStep.then(function () { return self.getCart(); }).then(function (cart2) {
        var codes = self.addonCodes(cart2);
        var sig = codes.slice().sort().join(',');
        var needSync = sig !== self.appliedSig() && (codes.length || self.appliedSig() !== '');
        var syncStep = needSync ? self.syncDiscountCodes() : Promise.resolve(null);

        return Promise.resolve(syncStep).then(function () {
          if (pend.length || needSync) {
            return self.refreshCartSections().then(function () { self.refreshCartCount(); });
          }
          return null;
        }).then(function () { return cart2; });
      }).then(function (cart2) {
        var state2 = self.patternsState(cart2);
        var offers = self.offersFor(state2);
        var hasAnchor = state2.bundleInCart || state2.inCart.length >= 1;
        if (!hasAnchor || offers.length === 0) return;
        self.renderCartPage(offers, state2.bundleInCart);
      });
    });
  }

  /* Dawn removes/changes line items via AJAX (no page reload), so watch
     the items area and re-evaluate the offer whenever the cart changes —
     e.g. removing the Playtime line brings its upsell row back. Our own
     block lives in the footer's page-width, outside the observed node,
     so re-rendering it can't retrigger the observer. */
  observeCartPage() {
    var target = document.getElementById('main-cart-items');
    if (!target) return;
    var self = this;
    new MutationObserver(function () {
      clearTimeout(self._cartPageTimer);
      self._cartPageTimer = setTimeout(function () { self.refreshCartPageUpsell(); }, 200);
    }).observe(target, { childList: true, subtree: true });
  }

  refreshCartPageUpsell() {
    var self = this;
    this.getCart().then(function (cart) {
      var existing = document.getElementById('sss-cart-upsell');

      if (!cart.items || !cart.items.length) {
        if (existing) existing.remove();
        return;
      }

      var state = self.patternsState(cart);
      var offers = self.offersFor(state);
      var hasAnchor = state.bundleInCart || state.inCart.length >= 1;

      /* keep discount codes + tracking attributes in sync when an add-on
         line was removed directly from the cart table (e.g. the x2 code
         must switch back to the single code) — and then RE-RENDER
         the totals, because Dawn already painted them before the new code
         was applied. The section refresh retriggers this observer once
         more, but the sig checks below make that second pass a no-op. */
      var sig = self.addonCodes(cart).slice().sort().join(',');
      var syncStep = (sig !== self.appliedSig())
        ? self.syncDiscountCodes()
            .then(function () { return self.refreshCartSections(); })
            .then(function () { self.refreshCartCount(); })
        : Promise.resolve();

      syncStep.then(function () {
        if (!hasAnchor || offers.length === 0) {
          if (existing) existing.remove();
          return;
        }
        var offerSig = offers.map(function (p) { return p.key; }).join(',');
        var current = document.getElementById('sss-cart-upsell');
        if (current && current.dataset.sig === offerSig) return;
        self.renderCartPage(offers, state.bundleInCart);
      });
    });
  }

  renderCartPage(offers, bundleInCart) {
    var self = this;
    Promise.all(offers.map(function (p) { return self.getProduct(p.handle); })).then(function (prods) {
      prods = prods.filter(Boolean);
      if (!prods.length) return;
      var old = document.getElementById('sss-cart-upsell');
      if (old) old.remove();
      var block = document.createElement('div');
      block.id = 'sss-cart-upsell';
      block.className = 'sss-upsell-wrap';
      block.dataset.sig = offers.map(function (p) { return p.key; }).join(',');
      block.innerHTML = self.upsellMarkup(prods, 'cart', bundleInCart);
      self.placeCartBlock(block);
      self.bindBumps(block, 'cart');
    });
  }

  placeCartBlock(block) {
    var tries = 0;
    function attempt() {
      // keep it INSIDE the page-width container so it isn't full browser width
      var pw = document.querySelector('#main-cart-items .page-width');
      if (pw) { pw.appendChild(block); return; }
      var footer = document.querySelector('#main-cart-footer .page-width');
      if (footer) { footer.insertBefore(block, footer.firstChild); return; }
      var footerSection = document.querySelector('#main-cart-footer');
      if (footerSection && footerSection.parentNode) { footerSection.parentNode.insertBefore(block, footerSection); return; }
      if (tries++ < 20) { setTimeout(attempt, 150); return; }
      document.body.appendChild(block);
    }
    attempt();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { new CartUpsell(); });
} else {
  new CartUpsell();
}
